/* ============================================================
   pdp-reveal.js — SHOGUN PDP リッチ再デザイン モーション基盤
   出典: docs/pdp-redesign/pdp-mock-template.html <script> の逐語移植
   スコープ: (a) .rv / .stamp / [data-sumie] / [data-closing] の IO reveal
             (b) サイドカード: FV通過後に出現 (rAF不要の passive scroll +
                 getBoundingClientRect — hidden tab でも動く実機堅牢方式)
             (c) ギャラリー placard キャプション同期
             (d) カード ATC の native form 配線 (fallback フォームのみ)
             (e) Instafeed lightbox 脱出ボタン (app の閉じるボタン不全への保険)
   規律: prefers-reduced-motion 全静止・passive listener・AbortController
   ============================================================ */
(function () {
  'use strict';

  var io = null;
  var controller = null;

  function teardown() {
    if (io) { io.disconnect(); io = null; }
    if (controller) { controller.abort(); controller = null; }
  }

  function init() {
    teardown();
    controller = new AbortController();
    var signal = controller.signal;

    var reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    var targets = document.querySelectorAll('.rv, .stamp, [data-sumie], [data-closing]');
    if (!reduce && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(function (es) {
        es.forEach(function (e) {
          if (!e.isIntersecting) return;
          e.target.classList.add('on');
          io.unobserve(e.target);
        });
      }, { rootMargin: '-10% 0px' });
      targets.forEach(function (el) { io.observe(el); });
    } else {
      targets.forEach(function (el) { el.classList.add('on'); });
    }

    /* side card: FV(=main product グリッド)通過後に出現。
       IO でなく scroll+rect (hidden tab でも確実 — floating-atc 実証済みパターン) */
    var anchor = document.querySelector('.product.grid') || document.querySelector('.product');
    var sc = document.getElementById('pdp-sidecard');
    if (anchor && sc) {
      var updateCard = function () {
        var passed = anchor.getBoundingClientRect().bottom <= 0;
        sc.classList.toggle('on', passed);
        sc.setAttribute('aria-hidden', passed ? 'false' : 'true');
      };
      window.addEventListener('scroll', updateCard, { passive: true, signal: signal });
      window.addEventListener('resize', updateCard, { passive: true, signal: signal });
      updateCard();
    }

    /* カード ATC の form 配線 (fallback 素フォームのみ)。
       注: form 要素の .id は <input name="id"> に隠蔽されるため getAttribute を使う */
    var nativeForm = document.querySelector('product-form form[data-type="add-to-cart-form"], form[data-type="add-to-cart-form"]');
    var nativeFormId = nativeForm ? nativeForm.getAttribute('id') : null;
    if (nativeFormId) {
      document.querySelectorAll('[data-pdp-card-atc]:not([form])').forEach(function (btn) {
        btn.setAttribute('form', nativeFormId);
      });
    }

    /* gallery placard: アクティブメディアの alt をキャプション表示。
       カーンFB 2026-08-30: 文字焼き込み画像 (data-placard-skip のファイル名に部分一致) では
       キャプションを出さない (画像内の文字と被るため)。alt が空の場合も非表示 (旧実装は
       前のキャプションが残る挙動だった)。 */
    var cap = document.getElementById('pdp-gallery-placard');
    var gallery = document.querySelector('media-gallery');
    if (cap && gallery) {
      var skipTokens = (cap.getAttribute('data-placard-skip') || '')
        .split(',')
        .map(function (s) { return s.trim(); })
        .filter(Boolean);
      /* 川下さんFB 2026-09-01(③④): 表示文の上書きマップ。
         1行 "ファイル名の一部 | 表示文"。alt には手を入れず (alt は画像の説明として正しい形で残す)、
         見えるキャプションだけを訴求文に差し替える。指定のない画像は従来どおり alt を出す。 */
      var captionMap = (cap.getAttribute('data-placard-map') || '')
        .split('\n')
        .map(function (line) {
          var i = line.indexOf('|');
          if (i === -1) return null;
          var key = line.slice(0, i).trim();
          var text = line.slice(i + 1).trim();
          return key && text ? { key: key, text: text } : null;
        })
        .filter(Boolean);
      /* アクティブ判定: SPスライダーのスワイプでは Dawn は is-active を動かさない
         (aria-current のみ更新) ため、スクロール可能な間は横スクロール位置を正とする。
         スクロールしないレイアウト (PCサムネイル) は従来どおり is-active。 */
      var currentImg = function () {
        var slider = gallery.querySelector('slider-component ul');
        if (slider && slider.scrollWidth > slider.clientWidth + 10) {
          var best = null;
          var bestDist = Infinity;
          gallery.querySelectorAll('.product__media-item').forEach(function (item) {
            var d = Math.abs(item.offsetLeft - slider.scrollLeft);
            if (d < bestDist) { bestDist = d; best = item; }
          });
          if (best) return best.querySelector('img');
        }
        return gallery.querySelector('.product__media-item.is-active img, [aria-hidden="false"] img') ||
               gallery.querySelector('.product__media-item img');
      };
      var sync = function () {
        var active = currentImg();
        if (!active) return;
        var src = active.currentSrc || active.src || '';
        var skip = skipTokens.some(function (t) { return src.indexOf(t) !== -1; });
        if (skip) {
          cap.hidden = true;
          return;
        }
        var override = null;
        for (var i = 0; i < captionMap.length; i++) {
          if (src.indexOf(captionMap[i].key) !== -1) { override = captionMap[i].text; break; }
        }
        var text = override || active.alt;
        if (!text) {
          cap.hidden = true;
          return;
        }
        cap.hidden = false;
        cap.textContent = text;
      };
      sync();
      /* 【重要】ここで requestAnimationFrame を挟まないこと。
         rAF は非表示タブ (バックグラウンドタブ・非表示のプレビューペイン) では発火しないため、
         キャプションが初期値のまま固まる (2026-09-01 実測: rAF 版では全12枚が1枚目のまま)。
         sync() は DOM 読み取り数回で十分に軽いので同期実行してよい。 */
      /* click は「押した直後」と「スライダーが動いた後」の2回引く。
         media-gallery.js の setActiveMedia はクラスを先に付け替え、スライダーのスクロールは
         window.setTimeout(…, 0) の中で走る。こちらは gallery でのバブリング受けなので
         Dawn のハンドラより後に setTimeout を積める = スクロール後に引き直せる。
         (2026-09-01 実測: 即時 sync だけだと全12枚が「1つ前の画像のキャプション」になった) */
      var syncSoon = function () {
        sync();
        setTimeout(sync, 0);
        setTimeout(sync, 150);
      };
      gallery.addEventListener('click', syncSoon, { signal: signal });
      var galleryViewer = gallery.querySelector('slider-component');
      if (galleryViewer) {
        galleryViewer.addEventListener('slideChanged', syncSoon, { signal: signal });
      }
      /* currentImg() は横スクロール位置からアクティブ画像を決めるので、
         スクロールが終わってからもう一度 sync する必要がある。
         media-gallery.js の setActiveMedia はクラスを先に付け替え、
         スライダーのスクロールは window.setTimeout の中で後から走るため、
         MutationObserver 経由の sync だけだと「1つ前の画像のキャプション」が残る
         (2026-09-01 実測: サムネイル送りで全12枚が1つずれた)。
         スライダー自身の scroll を購読して、実際に動いた後の位置で引き直す。スワイプ経路も同じ経路で拾える。 */
      var slider = gallery.querySelector('slider-component ul');
      if (slider) {
        slider.addEventListener('scroll', sync, { passive: true, signal: signal });
      }
      new MutationObserver(sync).observe(gallery, { attributes: true, subtree: true, attributeFilter: ['class', 'aria-hidden'] });
    }

    /* Instafeed lightbox 脱出保険: app の閉じるボタンが 0×0 になる環境向けに
       開いている間だけ自前の × を重ねる (app 内部 DOM は非改変・上に置くだけ)。
       Instafeed は非同期ロードのため wrapper 出現をリトライで待つ */
    var ifTries = 0;
    (function waitInstafeed() {
      if (document.getElementById('pdp-if-exit')) return;
      var ifWrapper = document.querySelector('.instafeed-modals-wrapper');
      if (!ifWrapper) {
        if (ifTries++ < 20) setTimeout(waitInstafeed, 1500);
        return;
      }
      setupInstafeedExit(ifWrapper);
    })();

    function setupInstafeedExit(ifWrapper) {
      var exitBtn = document.createElement('button');
      exitBtn.type = 'button';
      exitBtn.id = 'pdp-if-exit';
      exitBtn.setAttribute('aria-label', 'Close Instagram popup');
      exitBtn.textContent = '×';
      exitBtn.hidden = true;
      document.body.appendChild(exitBtn);

      var findOpen = function () {
        return Array.from(ifWrapper.querySelectorAll('.instafeed-lightbox')).find(function (l) {
          return getComputedStyle(l).display !== 'none';
        }) || null;
      };
      exitBtn.addEventListener('click', function () {
        var open = findOpen();
        if (open) {
          var closer = open.querySelector('[data-instafeed-close-id]');
          if (closer) closer.click();
          /* app 側ハンドラが不発でも確実に閉じる */
          if (getComputedStyle(open).display !== 'none') open.style.display = 'none';
        }
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
        exitBtn.hidden = true;
      });
      var syncExit = function () {
        var open = findOpen();
        exitBtn.hidden = !open;
        /* Instafeed wrapper は後着注入のため、同値 z-index の DOM 順で hit-test 負けする。
           開くたび body 末尾へ再配置して最前面を保証 (listener は appendChild で維持される) */
        if (open && exitBtn !== document.body.lastElementChild) document.body.appendChild(exitBtn);
      };
      /* childList 込み: 実機で lightbox が「ノード追加」で開くパターンも拾う */
      new MutationObserver(syncExit).observe(ifWrapper, { attributes: true, childList: true, subtree: true, attributeFilter: ['style', 'class'] });
      /* app は自 wrapper を body 末尾へ保ち続ける (~0.5s tick 駆動・実測)。同値 z-index の
         DOM 順タイブレークで負けないよう、body を監視して常に追随再配置する。
         app 側は observer 連鎖でなく tick のため無限ループにはならない (実測済み) */
      new MutationObserver(function () {
        if (!exitBtn.hidden && exitBtn !== document.body.lastElementChild) document.body.appendChild(exitBtn);
      }).observe(document.body, { childList: true });
      /* 最終保険: フィードのタップ後に遅延チェック (observer 不発でも必ず表示判定が走る) */
      document.addEventListener('click', function (e) {
        if (e.target && e.target.closest && e.target.closest('#insta-feed, .instafeed-new-layout-container')) {
          setTimeout(syncExit, 400);
          setTimeout(syncExit, 1200);
        }
      }, { capture: true, signal: signal });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', teardown);
})();
